import { useMediaQuery, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import { isIPad, isTablet, isMobilePhone } from "../utilities/deviceDetection";

interface DeviceInfo {
  isTabletDevice: boolean;
  isIPadDevice: boolean;
  isMobilePhoneDevice: boolean;
}

interface DeviceTypeResult {
  isMobile: boolean;
  isTablet: boolean;
  isIPad: boolean;
  isMobileOrTablet: boolean;
  isDesktop: boolean;
  shouldUseMobileUX: boolean;
}

/**
 * Custom hook to determine device type
 * Combines screen size (media query) with device detection (user agent)
 * to provide accurate device type detection
 * 
 * @returns Device type information
 * - isMobile: true for mobile phones based on media query
 * - isTablet: true for tablets (iPad, Android tablets) based on user agent
 * - isIPad: true specifically for iPad devices
 * - isMobileOrTablet: true for mobile phones OR tablets (combines media query + device detection)
 * - isDesktop: true for desktop devices
 * - shouldUseMobileUX: true if the device should use mobile UX (includes tablets)
 */
export function useDeviceType(): DeviceTypeResult {
  const theme = useTheme();
  
  // Media query for small and medium screens (mobile phones and tablets)
  // Using 'lg' breakpoint to include tablets in mobile UX
  const isMobileByScreenSize = useMediaQuery(theme.breakpoints.down("lg"));
  
  // Media query for medium and small screens (tablets and phones)
  const isTabletOrMobileByScreenSize = useMediaQuery(theme.breakpoints.down("lg"));
  
  // State for device detection (user agent based)
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isTabletDevice: false,
    isIPadDevice: false,
    isMobilePhoneDevice: false,
  });

  useEffect(() => {
    // Run device detection on client side only
    setDeviceInfo({
      isTabletDevice: isTablet(),
      isIPadDevice: isIPad(),
      isMobilePhoneDevice: isMobilePhone(),
    });
  }, []);

  // Combine media query with device detection
  // A device is considered "mobile or tablet" if:
  // 1. It's below the lg breakpoint (screen size), OR
  // 2. It's an iPad or tablet (regardless of screen size)
  const isMobileOrTablet = isTabletOrMobileByScreenSize || deviceInfo.isTabletDevice;
  
  // Should use mobile UX if it's a tablet OR mobile phone OR screen size is below lg breakpoint
  const useMobileUX = deviceInfo.isTabletDevice || deviceInfo.isMobilePhoneDevice || isMobileByScreenSize;

  return {
    isMobile: isMobileByScreenSize,
    isTablet: deviceInfo.isTabletDevice,
    isIPad: deviceInfo.isIPadDevice,
    isMobileOrTablet,
    isDesktop: !isMobileOrTablet,
    shouldUseMobileUX: useMobileUX,
  };
}
